/**
 * Multi-Chart Grid Component
 * Renders multiple charts in a grid layout with resizable panels
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChartPanelConfig, LayoutType } from '../types/chartLayout';
import LiveChart from './LiveChart';
import { CandleData } from '../types';

// Debounced symbol input component - only triggers onSymbolChange after user stops typing
const DebouncedSymbolInput: React.FC<{
  panelId: string;
  initialSymbol: string;
  onSymbolChange: (panelId: string, symbol: string) => void;
  disabled?: boolean;
  isActive?: boolean;
}> = React.memo(({ panelId, initialSymbol, onSymbolChange, disabled = false, isActive = false }) => {
  const [localSymbol, setLocalSymbol] = useState(initialSymbol);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastCommittedSymbol = useRef(initialSymbol);

  // Update local state if the initial symbol changes from outside (e.g., panel reset)
  useEffect(() => {
    if (initialSymbol !== lastCommittedSymbol.current) {
      setLocalSymbol(initialSymbol);
      lastCommittedSymbol.current = initialSymbol;
    }
  }, [initialSymbol]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newSymbol = e.target.value.toUpperCase();
    setLocalSymbol(newSymbol);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Set new timer - only trigger symbol change after 800ms of no typing
    debounceTimerRef.current = setTimeout(() => {
      if (newSymbol && newSymbol.length >= 1 && newSymbol !== lastCommittedSymbol.current) {
        console.log(`[MultiChartGrid] Symbol input committed: ${newSymbol}`);
        lastCommittedSymbol.current = newSymbol;
        onSymbolChange(panelId, newSymbol);
      }
    }, 800);
  };

  const handleBlur = () => {
    // Commit immediately on blur if there's a pending change
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
    if (localSymbol && localSymbol !== lastCommittedSymbol.current) {
      console.log(`[MultiChartGrid] Symbol input committed on blur: ${localSymbol}`);
      lastCommittedSymbol.current = localSymbol;
      onSymbolChange(panelId, localSymbol);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      // Commit immediately on Enter
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      if (localSymbol && localSymbol !== lastCommittedSymbol.current) {
        console.log(`[MultiChartGrid] Symbol input committed on Enter: ${localSymbol}`);
        lastCommittedSymbol.current = localSymbol;
        onSymbolChange(panelId, localSymbol);
      }
      (e.target as HTMLInputElement).blur();
    }
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <input
      type="text"
      value={localSymbol}
      onChange={handleChange}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      className={`px-2 py-1 bg-slate-800/90 backdrop-blur-sm border rounded text-xs font-bold w-20 focus:outline-none
        ${disabled 
          ? 'border-slate-800 text-slate-500 cursor-not-allowed opacity-60' 
          : isActive 
            ? 'border-emerald-600 text-white focus:ring-1 focus:ring-emerald-500' 
            : 'border-slate-700 text-white focus:ring-1 focus:ring-emerald-500'
        }`}
      placeholder="Symbol"
      title={disabled ? 'Symbol changes disabled during replay mode' : ''}
    />
  );
});

interface MultiChartGridProps {
  layoutType: LayoutType;
  panels: ChartPanelConfig[];
  onPanelUpdate: (panelId: string, updates: Partial<ChartPanelConfig>) => void;
  replayMode: 'off' | 'selecting' | 'active';
  // Chart data per panel
  panelData: Map<string, CandleData[]>;
  panelHistoryState: Map<string, { isFetching: boolean; hasMore: boolean }>;
  onSymbolChange: (panelId: string, symbol: string) => void;
  onTimeframeChange: (panelId: string, timeframe: string) => void;
  onLoadMoreHistory: (panelId: string, oldestTime: number) => void;
}

// Chart wrapper component that ensures proper dimensions
const ChartWrapper: React.FC<{
  data: CandleData[];
  ticker: string;
  timeframe: string;
  replayMode: 'off' | 'selecting' | 'active';
  onTickerChange: (ticker: string) => void;
  onTimeframeChange: (timeframe: string) => void;
  chartId: string;
  onLoadMore?: (oldestTime: number) => void;
  isFetchingMore?: boolean;
  hasMoreHistory?: boolean;
}> = React.memo(({ data, ticker, timeframe, replayMode, onTickerChange, onTimeframeChange, chartId, onLoadMore, isFetchingMore, hasMoreHistory }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;

    const checkDimensions = () => {
      if (containerRef.current) {
        const { clientWidth, clientHeight } = containerRef.current;
        
        if (clientWidth > 0 && clientHeight > 0 && !isReady) {
          // Add a small delay to ensure DOM is fully ready
          setTimeout(() => setIsReady(true), 100);
        }
      }
    };

    // Check immediately
    checkDimensions();

    // Check after a short delay for CSS to apply
    const timer = setTimeout(checkDimensions, 50);

    // Observe resize
    const resizeObserver = new ResizeObserver(checkDimensions);
    resizeObserver.observe(containerRef.current);

    return () => {
      clearTimeout(timer);
      resizeObserver.disconnect();
    };
  }, [ticker, isReady]);

  return (
    <div ref={containerRef} style={{ width: '100%', height: '100%', minHeight: '200px', position: 'relative', display: 'flex', flexDirection: 'column' }}>
      {isReady ? (
        <div style={{ flex: 1, width: '100%', height: '100%', minHeight: '200px', display: 'flex', flexDirection: 'column' }}>
          <LiveChart
            data={data}
            ticker={ticker}
            timeframe={timeframe}
            replayMode={replayMode}
            onTickerChange={onTickerChange}
            onTimeframeChange={onTimeframeChange}
            chartId={chartId}
            onLoadMore={onLoadMore}
            isFetchingMore={isFetchingMore}
            hasMoreHistory={hasMoreHistory}
          />
        </div>
      ) : (
        <div className="w-full h-full flex items-center justify-center text-slate-500 text-xs">
          Initializing chart...
        </div>
      )}
    </div>
  );
});

export const MultiChartGrid: React.FC<MultiChartGridProps> = ({
  layoutType,
  panels,
  onPanelUpdate,
  replayMode,
  panelData,
  panelHistoryState,
  onSymbolChange,
  onTimeframeChange,
  onLoadMoreHistory
}) => {
  const [activePanelId, setActivePanelId] = useState<string | null>(null);
  const [hoveredPanelId, setHoveredPanelId] = useState<string | null>(null);
  
  // Resize state for active panel
  const [panelSizes, setPanelSizes] = useState<Map<string, { width: number; height: number }>>(new Map());
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState<'right' | 'left' | 'top' | 'bottom' | 'corner-br' | 'corner-bl' | 'corner-tr' | 'corner-tl' | null>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const resizeStartRef = useRef<{ x: number; y: number; width: number; height: number } | null>(null);

  // Calculate grid layout
  const getGridLayout = () => {
    switch (layoutType) {
      case '1x1': return { rows: 1, cols: 1 };
      case '2x1': return { rows: 1, cols: 2 };
      case '1x2': return { rows: 2, cols: 1 };
      case '2x2': return { rows: 2, cols: 2 };
      case '3x1': return { rows: 1, cols: 3 };
      case '1x3': return { rows: 3, cols: 1 };
      case '3x2': return { rows: 2, cols: 3 };
      case '2x3': return { rows: 3, cols: 2 };
      case '4x1': return { rows: 1, cols: 4 };
      case '1x4': return { rows: 4, cols: 1 };
      default: return { rows: 1, cols: 1 };
    }
  };

  const { rows, cols } = getGridLayout();

  // Get panel position in grid
  const getPanelPosition = (index: number) => {
    const row = Math.floor(index / cols);
    const col = index % cols;
    return { row, col };
  };

  // Handle resize start
  const handleResizeStart = (e: React.MouseEvent, panelId: string, direction: 'right' | 'left' | 'top' | 'bottom' | 'corner-br' | 'corner-bl' | 'corner-tr' | 'corner-tl') => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!gridRef.current) return;
    
    const panelElement = gridRef.current.querySelector(`[data-panel-id="${panelId}"]`) as HTMLElement;
    if (!panelElement) return;
    
    const rect = panelElement.getBoundingClientRect();
    
    resizeStartRef.current = {
      x: e.clientX,
      y: e.clientY,
      width: rect.width,
      height: rect.height
    };
    
    setIsResizing(true);
    setResizeDirection(direction);
  };

  // Handle resize move
  useEffect(() => {
    if (!isResizing || !activePanelId || !resizeStartRef.current) return;
    
    const handleMouseMove = (e: MouseEvent) => {
      if (!resizeStartRef.current || !gridRef.current) return;
      
      const deltaX = e.clientX - resizeStartRef.current.x;
      const deltaY = e.clientY - resizeStartRef.current.y;
      
      const gridRect = gridRef.current.getBoundingClientRect();
      const minWidth = 300; // Minimum panel width
      const minHeight = 250; // Minimum panel height
      
      // Calculate max dimensions based on grid size and other panels' minimum sizes
      const gap = 4 * (cols - 1); // Total gap width
      const padding = 8; // Total padding
      
      // Get the position of the active panel
      const activePanelIndex = panels.findIndex(p => p.id === activePanelId);
      const { row: activeRow, col: activeCol } = getPanelPosition(activePanelIndex);
      
      // Map panel IDs to their grid positions
      const panelPositions = new Map<string, { row: number; col: number }>();
      panels.forEach((panel, index) => {
        panelPositions.set(panel.id, getPanelPosition(index));
      });
      
      // Calculate max width: account for existing custom sizes in other columns
      let maxWidth: number;
      if (cols === 1) {
        maxWidth = gridRect.width - padding;
      } else {
        // Track which columns have custom sizes
        const customColSizes = new Map<number, number>();
        panelSizes.forEach((size, panelId) => {
          const pos = panelPositions.get(panelId);
          if (pos && pos.col !== activeCol) {
            const existingWidth = customColSizes.get(pos.col);
            if (existingWidth === undefined || size.width > existingWidth) {
              customColSizes.set(pos.col, size.width);
            }
          }
        });
        
        // Calculate total space needed for all other columns
        let otherColsSpace = 0;
        for (let c = 0; c < cols; c++) {
          if (c !== activeCol) {
            // Use custom size if exists, otherwise use minimum
            otherColsSpace += customColSizes.get(c) || 250;
          }
        }
        
        const totalGaps = gap;
        const availableForResize = gridRect.width - padding - totalGaps - otherColsSpace;
        maxWidth = Math.max(minWidth, availableForResize);
      }
      
      // Calculate max height: account for existing custom sizes in other rows
      let maxHeight: number;
      if (rows === 1) {
        maxHeight = gridRect.height - padding;
      } else {
        // Track which rows have custom sizes
        const customRowSizes = new Map<number, number>();
        panelSizes.forEach((size, panelId) => {
          const pos = panelPositions.get(panelId);
          if (pos && pos.row !== activeRow) {
            const existingHeight = customRowSizes.get(pos.row);
            if (existingHeight === undefined || size.height > existingHeight) {
              customRowSizes.set(pos.row, size.height);
            }
          }
        });
        
        // Calculate total space needed for all other rows
        let otherRowsSpace = 0;
        for (let r = 0; r < rows; r++) {
          if (r !== activeRow) {
            // Use custom size if exists, otherwise use minimum
            otherRowsSpace += customRowSizes.get(r) || 250;
          }
        }
        
        const totalGaps = gap;
        const availableForResize = gridRect.height - padding - totalGaps - otherRowsSpace;
        maxHeight = Math.max(minHeight, availableForResize);
      }
      
      let newWidth = resizeStartRef.current.width;
      let newHeight = resizeStartRef.current.height;
      
      // Handle horizontal resizing
      if (resizeDirection === 'right' || resizeDirection === 'corner-br' || resizeDirection === 'corner-tr') {
        newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStartRef.current.width + deltaX));
      } else if (resizeDirection === 'left' || resizeDirection === 'corner-bl' || resizeDirection === 'corner-tl') {
        newWidth = Math.max(minWidth, Math.min(maxWidth, resizeStartRef.current.width - deltaX));
      }
      
      // Handle vertical resizing
      if (resizeDirection === 'bottom' || resizeDirection === 'corner-br' || resizeDirection === 'corner-bl') {
        newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStartRef.current.height + deltaY));
      } else if (resizeDirection === 'top' || resizeDirection === 'corner-tr' || resizeDirection === 'corner-tl') {
        newHeight = Math.max(minHeight, Math.min(maxHeight, resizeStartRef.current.height - deltaY));
      }
      
      setPanelSizes(prev => {
        const newMap = new Map(prev);
        newMap.set(activePanelId, { width: newWidth, height: newHeight });
        return newMap;
      });
    };
    
    const handleMouseUp = () => {
      setIsResizing(false);
      setResizeDirection(null);
      resizeStartRef.current = null;
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, activePanelId, resizeDirection]);

  // Calculate grid template based on ALL panel sizes (not just active)
  const getGridStyle = () => {
    if (panelSizes.size === 0 || !gridRef.current) {
      return {
        gridTemplateRows: `repeat(${rows}, minmax(200px, 1fr))`,
        gridTemplateColumns: `repeat(${cols}, 1fr)`
      };
    }
    
    const gridRect = gridRef.current.getBoundingClientRect();
    const gap = 4; // 1px gap * 4 (for 2x2 grid, adjust based on layout)
    const padding = 8; // p-1 = 4px on each side
    
    // Map panel IDs to their grid positions
    const panelPositions = new Map<string, { row: number; col: number }>();
    panels.forEach((panel, index) => {
      panelPositions.set(panel.id, getPanelPosition(index));
    });
    
    // Build column template - check each column for custom-sized panels
    const colTemplate: string[] = [];
    const customColSizes = new Map<number, number>();
    
    // Find all columns with custom sizes (use max if multiple panels in same column)
    panelSizes.forEach((size, panelId) => {
      const pos = panelPositions.get(panelId);
      if (pos) {
        const existingWidth = customColSizes.get(pos.col);
        if (existingWidth === undefined || size.width > existingWidth) {
          customColSizes.set(pos.col, size.width);
        }
      }
    });
    
    if (cols > 1 && customColSizes.size > 0) {
      const availableWidth = gridRect.width - padding - (gap * (cols - 1));
      let totalCustomWidth = 0;
      customColSizes.forEach((width, col) => {
        // Don't count the last column in total if it has custom size (it will use 1fr)
        if (col !== cols - 1) {
          totalCustomWidth += width;
        }
      });
      
      const flexColsCount = cols - customColSizes.size;
      const remainingWidth = Math.max(250 * flexColsCount, availableWidth - totalCustomWidth);
      const flexColWidth = flexColsCount > 0 ? remainingWidth / flexColsCount : 0;
      
      for (let c = 0; c < cols; c++) {
        if (customColSizes.has(c)) {
          // Last column always uses 1fr to fill remaining space and stick to edge
          if (c === cols - 1) {
            colTemplate.push(`minmax(250px, 1fr)`);
          } else {
            colTemplate.push(`${customColSizes.get(c)}px`);
          }
        } else {
          colTemplate.push(`minmax(250px, ${flexColWidth}px)`);
        }
      }
    } else if (cols === 1) {
      colTemplate.push('1fr');
    } else {
      for (let c = 0; c < cols; c++) {
        colTemplate.push('1fr');
      }
    }
    
    // Build row template - check each row for custom-sized panels
    const rowTemplate: string[] = [];
    const customRowSizes = new Map<number, number>();
    
    // Find all rows with custom sizes (use max if multiple panels in same row)
    panelSizes.forEach((size, panelId) => {
      const pos = panelPositions.get(panelId);
      if (pos) {
        const existingHeight = customRowSizes.get(pos.row);
        if (existingHeight === undefined || size.height > existingHeight) {
          customRowSizes.set(pos.row, size.height);
        }
      }
    });
    
    if (rows > 1 && customRowSizes.size > 0) {
      const availableHeight = gridRect.height - padding - (gap * (rows - 1));
      let totalCustomHeight = 0;
      customRowSizes.forEach((height, row) => {
        // Don't count the last row in total if it has custom size (it will use 1fr)
        if (row !== rows - 1) {
          totalCustomHeight += height;
        }
      });
      
      const flexRowsCount = rows - customRowSizes.size;
      const remainingHeight = Math.max(250 * flexRowsCount, availableHeight - totalCustomHeight);
      const flexRowHeight = flexRowsCount > 0 ? remainingHeight / flexRowsCount : 0;
      
      for (let r = 0; r < rows; r++) {
        if (customRowSizes.has(r)) {
          // Last row always uses 1fr to fill remaining space and stick to edge
          if (r === rows - 1) {
            rowTemplate.push(`minmax(250px, 1fr)`);
          } else {
            rowTemplate.push(`${customRowSizes.get(r)}px`);
          }
        } else {
          rowTemplate.push(`minmax(250px, ${flexRowHeight}px)`);
        }
      }
    } else if (rows === 1) {
      rowTemplate.push('1fr');
    } else {
      for (let r = 0; r < rows; r++) {
        rowTemplate.push('1fr');
      }
    }
    
    return {
      gridTemplateRows: rowTemplate.join(' '),
      gridTemplateColumns: colTemplate.join(' ')
    };
  };

  // Get cursor style based on resize direction
  const getCursor = () => {
    if (!isResizing) return 'default';
    switch (resizeDirection) {
      case 'right':
      case 'left':
        return 'ew-resize';
      case 'top':
      case 'bottom':
        return 'ns-resize';
      case 'corner-br':
      case 'corner-tl':
        return 'nwse-resize';
      case 'corner-bl':
      case 'corner-tr':
        return 'nesw-resize';
      default:
        return 'default';
    }
  };

  return (
    <div 
      ref={gridRef}
      className="w-full h-full grid gap-1 bg-slate-950 p-1 min-h-[400px]"
      style={{
        ...getGridStyle(),
        cursor: getCursor()
      }}
    >
      {panels.map((panel, index) => {
        const isActive = activePanelId === panel.id;
        const isHovered = hoveredPanelId === panel.id;
        const chartData = panelData.get(panel.id) || [];
        const historyState = panelHistoryState.get(panel.id) || { isFetching: false, hasMore: true };

        return (
          <div
            key={panel.id}
            data-panel-id={panel.id}
            className={`relative bg-slate-900 rounded-lg overflow-hidden transition-all min-h-[200px] ${
              isActive 
                ? 'ring-2 ring-emerald-500 shadow-lg shadow-emerald-500/20' 
                : isHovered
                ? 'ring-1 ring-slate-600'
                : 'ring-1 ring-slate-800'
            }`}
            onClick={() => setActivePanelId(panel.id)}
            onMouseEnter={() => setHoveredPanelId(panel.id)}
            onMouseLeave={() => setHoveredPanelId(null)}
          >
            {/* Panel Header */}
            <div className="absolute top-2 left-2 right-2 z-20 flex items-center justify-between pointer-events-none">
              <div className="flex items-center gap-2 pointer-events-auto">
                {/* Symbol Input - Debounced to prevent loading while typing */}
                {/* In replay mode, symbol input is disabled for non-active panels */}
                <DebouncedSymbolInput
                  panelId={panel.id}
                  initialSymbol={panel.symbol}
                  onSymbolChange={onSymbolChange}
                  disabled={replayMode === 'active' || replayMode === 'selecting'}
                  isActive={isActive}
                />
                
                {/* Timeframe Selector - disabled during replay mode */}
                <select
                  value={panel.timeframe}
                  onChange={(e) => onTimeframeChange(panel.id, e.target.value)}
                  disabled={replayMode === 'active' || replayMode === 'selecting'}
                  className={`px-2 py-1 bg-slate-800/90 backdrop-blur-sm border rounded text-xs font-medium focus:outline-none
                    ${(replayMode === 'active' || replayMode === 'selecting')
                      ? 'border-slate-800 text-slate-500 cursor-not-allowed opacity-60'
                      : 'border-slate-700 text-slate-300 focus:ring-1 focus:ring-emerald-500'
                    }`}
                  title={(replayMode === 'active' || replayMode === 'selecting') ? 'Timeframe changes disabled during replay mode' : ''}
                >
                  <option value="1m">1m</option>
                  <option value="5m">5m</option>
                  <option value="15m">15m</option>
                  <option value="30m">30m</option>
                  <option value="1h">1h</option>
                  <option value="4h">4h</option>
                  <option value="1d">1D</option>
                  <option value="1w">1W</option>
                </select>
              </div>

              {/* Panel Number Badge */}
              <div className="px-2 py-1 bg-slate-800/90 backdrop-blur-sm border border-slate-700 rounded text-[10px] font-bold text-slate-500">
                #{index + 1}
              </div>
            </div>

            {/* Chart */}
            <div className="absolute inset-0" style={{ paddingTop: '48px' }}>
              {/* Show chart if we have data OR if we're in replay mode (empty chart is valid for replay start) */}
              {(chartData.length > 0 || replayMode === 'active' || replayMode === 'selecting') ? (
                <ChartWrapper
                  data={chartData}
                  ticker={panel.symbol}
                  timeframe={panel.timeframe}
                  replayMode={replayMode}
                  onTickerChange={(newTicker) => onSymbolChange(panel.id, newTicker)}
                  onTimeframeChange={(newTimeframe) => onTimeframeChange(panel.id, newTimeframe)}
                  chartId={panel.id}
                  onLoadMore={(oldestTime) => onLoadMoreHistory(panel.id, oldestTime)}
                  isFetchingMore={historyState.isFetching}
                  hasMoreHistory={historyState.hasMore}
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-slate-500 text-sm">
                  <div className="text-center">
                    <svg className="w-8 h-8 mx-auto mb-2 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Loading {panel.symbol}...
                  </div>
                </div>
              )}
            </div>

            {/* Active Indicator */}
            {isActive && (
              <div className="absolute bottom-2 left-2 px-2 py-1 bg-emerald-500/20 border border-emerald-500 rounded text-[10px] font-bold text-emerald-400">
                ACTIVE
              </div>
            )}

            {/* Resize Handles - Only show for active panel, position-aware */}
            {isActive && (() => {
              const { row: panelRow, col: panelCol } = getPanelPosition(index);
              const isRightEdge = panelCol === cols - 1;
              const isLeftEdge = panelCol === 0;
              const isBottomEdge = panelRow === rows - 1;
              const isTopEdge = panelRow === 0;
              
              // Check if resize is possible in each direction
              if (!gridRef.current) return null;
              
              const gridRect = gridRef.current.getBoundingClientRect();
              const gap = 4 * (cols - 1);
              const padding = 8;
              const currentSize = panelSizes.get(panel.id);
              const currentWidth = currentSize?.width || gridRect.width / cols;
              const currentHeight = currentSize?.height || gridRect.height / rows;
              
              // Map panel positions
              const panelPositions = new Map<string, { row: number; col: number }>();
              panels.forEach((p, idx) => {
                panelPositions.set(p.id, getPanelPosition(idx));
              });
              
              // Calculate max possible width
              const customColSizes = new Map<number, number>();
              panelSizes.forEach((size, panelId) => {
                const pos = panelPositions.get(panelId);
                if (pos && pos.col !== panelCol) {
                  const existingWidth = customColSizes.get(pos.col);
                  if (existingWidth === undefined || size.width > existingWidth) {
                    customColSizes.set(pos.col, size.width);
                  }
                }
              });
              
              let otherColsSpace = 0;
              for (let c = 0; c < cols; c++) {
                if (c !== panelCol) {
                  otherColsSpace += customColSizes.get(c) || 250;
                }
              }
              const maxPossibleWidth = cols === 1 ? gridRect.width - padding : gridRect.width - padding - gap - otherColsSpace;
              
              // Calculate max possible height
              const customRowSizes = new Map<number, number>();
              panelSizes.forEach((size, panelId) => {
                const pos = panelPositions.get(panelId);
                if (pos && pos.row !== panelRow) {
                  const existingHeight = customRowSizes.get(pos.row);
                  if (existingHeight === undefined || size.height > existingHeight) {
                    customRowSizes.set(pos.row, size.height);
                  }
                }
              });
              
              let otherRowsSpace = 0;
              for (let r = 0; r < rows; r++) {
                if (r !== panelRow) {
                  otherRowsSpace += customRowSizes.get(r) || 250;
                }
              }
              const maxPossibleHeight = rows === 1 ? gridRect.height - padding : gridRect.height - padding - gap - otherRowsSpace;
              
              // Check if resize is possible (allow 10px threshold)
              const canResizeRight = !isRightEdge && currentWidth < maxPossibleWidth - 10;
              const canResizeLeft = !isLeftEdge && currentWidth < maxPossibleWidth - 10;
              const canResizeBottom = !isBottomEdge && currentHeight < maxPossibleHeight - 10;
              const canResizeTop = !isTopEdge && currentHeight < maxPossibleHeight - 10;
              
              return (
                <>
                  {/* Horizontal resize handles */}
                  {canResizeRight && (
                    <div
                      className="absolute top-0 right-0 w-2 h-full cursor-ew-resize hover:bg-emerald-500/30 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'right')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-emerald-500/50 rounded-l" />
                    </div>
                  )}
                  
                  {canResizeLeft && (
                    <div
                      className="absolute top-0 left-0 w-2 h-full cursor-ew-resize hover:bg-emerald-500/30 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'left')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-12 bg-emerald-500/50 rounded-r" />
                    </div>
                  )}

                  {/* Vertical resize handles */}
                  {canResizeBottom && (
                    <div
                      className="absolute bottom-0 left-0 h-2 w-full cursor-ns-resize hover:bg-emerald-500/30 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'bottom')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-12 bg-emerald-500/50 rounded-t" />
                    </div>
                  )}
                  
                  {canResizeTop && (
                    <div
                      className="absolute top-0 left-0 h-2 w-full cursor-ns-resize hover:bg-emerald-500/30 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'top')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute top-0 left-1/2 -translate-x-1/2 h-1 w-12 bg-emerald-500/50 rounded-b" />
                    </div>
                  )}

                  {/* Corner resize handles */}
                  {canResizeBottom && canResizeRight && (
                    <div
                      className="absolute bottom-0 right-0 w-4 h-4 cursor-nwse-resize hover:bg-emerald-500/50 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'corner-br')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute bottom-1 right-1 w-3 h-3 border-r-2 border-b-2 border-emerald-500 rounded-br" />
                    </div>
                  )}
                  
                  {canResizeBottom && canResizeLeft && (
                    <div
                      className="absolute bottom-0 left-0 w-4 h-4 cursor-nesw-resize hover:bg-emerald-500/50 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'corner-bl')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute bottom-1 left-1 w-3 h-3 border-l-2 border-b-2 border-emerald-500 rounded-bl" />
                    </div>
                  )}
                  
                  {canResizeTop && canResizeRight && (
                    <div
                      className="absolute top-0 right-0 w-4 h-4 cursor-nesw-resize hover:bg-emerald-500/50 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'corner-tr')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute top-1 right-1 w-3 h-3 border-r-2 border-t-2 border-emerald-500 rounded-tr" />
                    </div>
                  )}
                  
                  {canResizeTop && canResizeLeft && (
                    <div
                      className="absolute top-0 left-0 w-4 h-4 cursor-nwse-resize hover:bg-emerald-500/50 transition-colors z-30"
                      onMouseDown={(e) => handleResizeStart(e, panel.id, 'corner-tl')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="absolute top-1 left-1 w-3 h-3 border-l-2 border-t-2 border-emerald-500 rounded-tl" />
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        );
      })}
    </div>
  );
};
